import React, { useState, useEffect } from 'react'
import dayjs, { Dayjs } from 'dayjs'
import utc from 'dayjs/plugin/utc'
dayjs.extend(utc)

import {
    createSupplierCallback,
    getSupplierCallbacks,
    deleteSupplierCallback,
    getCallbacksCalendarId,
    getBackendGoogleAccessToken,
    toastSuccess,
} from '../../api'
import { toast } from 'react-toastify'
import { User, SupplierCallback, JsonSupplierCallback } from '../../models'
import { TooltipWrapper } from 'components/common'

interface ScheduleCallbackFormProps {
    supplierId: number
    supplierName: string
    users: User[]
}

export const ScheduleCallbackForm = (props: ScheduleCallbackFormProps) => {
    const [scheduledTime, setScheduledTime] = useState<Dayjs | null>(null)
    const [reason, setReason] = useState<string>('')
    const [assignedTo, setAssignedTo] = useState<number | ''>('')
    const [loading, setLoading] = useState<boolean>(false)
    const [supplierCallbacks, setSupplierCallbacks] = useState<SupplierCallback[]>([])
    const [calendarId, setCalendarId] = useState<string>('')
    const [accessToken, setAccessToken] = useState<string | null>(null)

    const users = props.users

    useEffect(() => {
        getCallbacksCalendarId()
            .then(({ data }) => setCalendarId(data.calendarId))
            .catch(() => {})
    }, [])

    useEffect(() => {
        getSupplierCallbacks(props.supplierId.toString())
            .then((response) => {
                if (response.status === 200) {
                    setSupplierCallbacks(
                        response.data.map(
                            (callback: JsonSupplierCallback) => new SupplierCallback(callback),
                        ),
                    )
                }
            })
            .catch(() => {})
    }, [props.supplierId, loading])

    useEffect(() => {
        getBackendGoogleAccessToken()
            .then((response) => {
                const token = response.data.accessToken

                setAccessToken(token)
            })
            .catch(() => {})
    }, [])

    const handleGoogleSignIn = () => {
        window.location.href = '/api/google/oauth'
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!scheduledTime || !reason.trim() || !assignedTo) {
            toast.error('Please fill in all required fields.')
            return
        }

        setLoading(true)
        try {
            await createSupplierCallback({
                scheduledTime: scheduledTime.format(),
                reason: reason,
                supplierId: props.supplierId,
                assignedTo: Number(assignedTo),
            })

            const event = {
                summary: `Callback for ${props.supplierName} assigned to ${users.find((user) => user.id === assignedTo)?.name}`,
                start: {
                    dateTime: scheduledTime.toISOString(),
                    timeZone: 'UTC',
                },
                end: {
                    dateTime: scheduledTime.add(1, 'hour').toISOString(),
                    timeZone: 'UTC',
                },
                description: reason,
            }

            const insertEvent = async (calendarId: string) => {
                const response = await fetch(
                    `https://www.googleapis.com/calendar/v3/calendars/${calendarId}/events`,
                    {
                        method: 'POST',
                        headers: {
                            Authorization: `Bearer ${accessToken}`,
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify(event),
                    },
                )

                if (!response.ok) {
                    throw new Error(`Failed to create event: ${response.statusText}`)
                }

                return response.json()
            }

            const [individualEvent, groupEvent] = await Promise.all([
                insertEvent('primary'),
                insertEvent(calendarId),
            ])

            console.log('Individual event created:', individualEvent)
            console.log('Group event created:', groupEvent)

            toastSuccess('Callback scheduled successfully.')
        } catch (error: any) {
            if (!error?.response) {
                toast.error('Failed to schedule callback.')
            }
        } finally {
            setLoading(false)
        }
    }

    const handleDelete = async (callbackId: number) => {
        setLoading(true)
        try {
            await deleteSupplierCallback(callbackId)
            setSupplierCallbacks(
                supplierCallbacks.filter((callback) => callback.id !== callbackId),
            )
            setLoading(false)
            toastSuccess('Callback deleted successfully.')
        } catch (error) {
            setLoading(false)
        }
    }

    const disableSubmitMessage = loading
        ? 'Scheduling...'
        : 'Please sign in with Google first.'

    return (
        <div className="card p-4">
            <h3>Schedule Call Back</h3>
            <div className="schedule-callback-form">
                <form onSubmit={handleSubmit}>
                    <div className="form-group mb-3">
                        <label htmlFor="assignedTo">Assign To</label>
                        <select
                            id="assignedTo"
                            className="form-control"
                            value={assignedTo}
                            onChange={(e) =>
                                setAssignedTo(
                                    e.target.value === '' ? '' : Number(e.target.value),
                                )
                            }
                            required
                        >
                            <option value="">Select a User</option>
                            {users
                                .filter(
                                    (user) =>
                                        user.is_active &&
                                        user.roles.some(
                                            (role) => role.name === 'callbackagent',
                                        ),
                                )
                                .map((user) => (
                                    <option key={user.id} value={user.id}>
                                        {user.name}
                                    </option>
                                ))}
                        </select>
                    </div>
                    <div className="form-group mb-3">
                        <label htmlFor="reason">Reason</label>
                        <textarea
                            id="reason"
                            className="form-control"
                            value={reason}
                            onChange={(e) => setReason(e.target.value.slice(0, 255))}
                            required
                        />
                    </div>
                    <div className="form-group mb-3">
                        <label htmlFor="scheduledTime">Scheduled Time</label>
                        <input
                            type="datetime-local"
                            id="scheduledTime"
                            onChange={(e) => {
                                if (e.target.value) {
                                    setScheduledTime(dayjs(e.target.value))
                                } else {
                                    setScheduledTime(null)
                                }
                            }}
                            value={
                                scheduledTime ? scheduledTime.format('YYYY-MM-DDTHH:mm') : ''
                            }
                            className="form-control"
                            required
                        />
                    </div>
                    <div style={{ display: 'flex', gap: '1rem' }}>
                        <TooltipWrapper
                            condition={loading || !accessToken}
                            element={
                                <button
                                    className="btn btn-primary"
                                    disabled={loading || !accessToken}
                                >
                                    Schedule Callback
                                </button>
                            }
                            message={disableSubmitMessage}
                        />
                        {!accessToken && (
                            <button
                                type="button"
                                className="btn btn-warning"
                                onClick={handleGoogleSignIn}
                            >
                                Sign in with Google
                            </button>
                        )}
                    </div>
                </form>
            </div>
            <div className="callbacks-list pt-4">
                <h3 className="mb-3 ">Scheduled Callbacks</h3>
                {supplierCallbacks.length > 0 ? (
                    <ul className="list-group">
                        {supplierCallbacks
                            .sort(
                                (a, b) =>
                                    dayjs.utc(a.scheduledTime).unix() -
                                    dayjs.utc(b.scheduledTime).unix(),
                            )
                            .map((callback) => {
                                return (
                                    <li
                                        key={callback.id}
                                        className="list-group-item d-flex justify-content-between align-items-start"
                                    >
                                        <div className="ms-2 me-auto">
                                            <div className="fw-bold">
                                                Time:{' '}
                                                {dayjs
                                                    .utc(callback.scheduledTime)
                                                    .local()
                                                    .format('YYYY-MM-DD hh:mm')}
                                            </div>
                                            <div>
                                                Assigned To:{' '}
                                                {
                                                    users.find(
                                                        (user) =>
                                                            user.id === callback.assignedTo,
                                                    )?.name
                                                }
                                            </div>
                                            <div>Reason: {callback.reason}</div>
                                        </div>
                                        <button
                                            onClick={() => handleDelete(callback.id)}
                                            className="btn btn-danger btn-sm"
                                        >
                                            Delete
                                        </button>
                                    </li>
                                )
                            })}
                    </ul>
                ) : (
                    <p>No callbacks scheduled.</p>
                )}
            </div>
        </div>
    )
}
